import { useEffect, useRef, useState, useCallback } from 'react';
import { ScreenSize, useGetScreenSize } from '../../../hooks';

const INTERACTIVE_PARENT_TAGS = ['BUTTON', 'A'];
const ANIMATION_TAGS = ['animate', 'animateTransform', 'animateMotion'];

export function useAnimatedIconControl(enabled: boolean) {
    const ref = useRef<HTMLSpanElement>(null);
    const isAnimating = useRef(false);
    const hasBeenAnimated = useRef(false);

    const [shouldShowAnimated, setShouldShowAnimated] = useState(false);

    const screenSize = useGetScreenSize();
    const isTouchDevice = screenSize === ScreenSize.Mobile || screenSize === ScreenSize.Tablet;

    const switchToAnimated = useCallback(() => {
        if (!hasBeenAnimated.current) {
            hasBeenAnimated.current = true;
            setShouldShowAnimated(true);
        }
    }, []);

    const startAnimation = useCallback(() => {
        if (!ref.current) {
            return;
        }

        switchToAnimated();

        if (isAnimating.current) {
            return;
        }

        const svg = ref.current.querySelector('svg');
        const animationElements = svg?.querySelectorAll(ANIMATION_TAGS.join(', '));

        if (!svg || !animationElements?.length) {
            return;
        }

        isAnimating.current = true;

        animationElements.forEach(element => {
            try {
                (element as SVGAnimationElement).beginElement();
            } catch (error) {
                console.warn('Failed to start SVG animation:', error);
            }
        });

        const firstAnimation = animationElements[0] as SVGAnimationElement;

        const onAnimationEnd = () => {
            isAnimating.current = false;
            firstAnimation.removeEventListener('endEvent', onAnimationEnd);
        };

        firstAnimation.addEventListener('endEvent', onAnimationEnd);
    }, [switchToAnimated]);

    useEffect(() => {
        if (!enabled || !ref.current) {
            return;
        }

        const parent = ref.current.parentElement;

        if (!(parent && INTERACTIVE_PARENT_TAGS.includes(parent.tagName))) {
            return;
        }

        if (isTouchDevice) {
            parent.addEventListener('touchstart', startAnimation, { passive: true });

            return () => {
                parent.removeEventListener('touchstart', startAnimation);
            };
        }

        parent.addEventListener('mouseenter', startAnimation, { passive: true });

        return () => {
            parent.removeEventListener('mouseenter', startAnimation);
        };
    }, [enabled, isTouchDevice, startAnimation]);

    return { ref, shouldShowAnimated };
}
